import { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Upload,
  CheckCheck,
  Coins,
  FileSpreadsheet,
  Clock,
  ShieldCheck,
  RefreshCw,
  Search,
  Check,
  X,
  FileText,
} from 'lucide-react';
import {
  fetchPaymentSummary,
  fetchPaymentSettlements,
  fetchShippingHistory,
  fetchShippingCarriers,
  uploadCarrierCsv,
  uploadSettlementCsv,
  approvePayment,
} from '../api.js';
import { PageHeader } from '../ui/PageHeader.js';
import { Button } from '../ui/Button.js';
import { Panel } from '../ui/panels.js';
import { TableShell, Table, THead, TBody, Tr, Th, Td } from '../ui/table.js';
import { formatTnd } from '../lib/format.js';


function formatDateFr(isoDate: string | null | undefined): string {
  if (!isoDate) return '—';
  const clean = isoDate.split('T')[0];
  const parts = clean?.split('-');
  if (parts && parts.length === 3 && parts[0]?.length === 4) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return isoDate;
}

export function PaymentsReconciliationPage(): React.ReactElement {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const settlementFileInputRef = useRef<HTMLInputElement>(null);

  const [selectedCarrier, setSelectedCarrier] = useState<string>('all');
  const [searchSettlement, setSearchSettlement] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'pending' | 'approved'>('all');

  // Approval Modal State
  const [approvalModalOpen, setApprovalModalOpen] = useState(false);
  const [targetSettlementId, setTargetSettlementId] = useState<number | null>(null);
  const [approvalBankRef, setApprovalBankRef] = useState('');
  const [approvalNotes, setApprovalNotes] = useState('');

  // Queries
  const carriersQuery = useQuery({
    queryKey: ['shipping', 'carriers'],
    queryFn: fetchShippingCarriers,
  });

  const paymentsSummaryQuery = useQuery({
    queryKey: ['shipping', 'payments-summary', selectedCarrier],
    queryFn: () => fetchPaymentSummary(selectedCarrier),
  });

  const settlementsQuery = useQuery({
    queryKey: ['shipping', 'settlements'],
    queryFn: fetchPaymentSettlements,
  });

  const historyQuery = useQuery({
    queryKey: ['shipping', 'history'],
    queryFn: fetchShippingHistory,
  });

  // Mutations
  const uploadDeliveryMutation = useMutation({
    mutationFn: uploadCarrierCsv,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shipping'] });
      alert('Fichier de dépose importé avec succès.');
    },
    onError: (err: Error) => {
      alert(`Erreur d'importation : ${err.message}`);
    },
  });

  const uploadSettlementMutation = useMutation({
    mutationFn: uploadSettlementCsv,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['shipping'] });
      alert(`Bordereau importé avec succès : ${data.parcelCount} colis enregistrés (${data.matchedInShiparon} rapprochés).`);
    },
    onError: (err: Error) => {
      alert(`Erreur d'importation du bordereau : ${err.message}`);
    },
  });

  const approveMutation = useMutation({
    mutationFn: approvePayment,
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['shipping'] });
      setApprovalModalOpen(false);
      setApprovalBankRef('');
      setApprovalNotes('');
      setTargetSettlementId(null);
      alert(`Bordereau validé : ${res.updatedCount} colis marqués Encaissés & Validés MaPara.`);
    },
    onError: (err: Error) => {
      alert(`Erreur lors de la validation : ${err.message}`);
    },
  });

  const filteredSettlements = (settlementsQuery.data?.settlements ?? []).filter((s) => {
    if (selectedCarrier !== 'all' && s.carrierName.toLowerCase() !== selectedCarrier.toLowerCase()) {
      return false;
    }
    if (filterStatus === 'pending' && s.status === 'approved') return false;
    if (filterStatus === 'approved' && s.status !== 'approved') return false;
    if (searchSettlement.trim()) {
      const q = searchSettlement.toLowerCase().trim();
      const matchFile = (s.filename || '').toLowerCase().includes(q);
      const matchCarrier = (s.carrierName || '').toLowerCase().includes(q);
      const matchRef = (s.bankRef || '').toLowerCase().includes(q);
      return matchFile || matchCarrier || matchRef;
    }
    return true;
  });

  const paymentSummary = paymentsSummaryQuery.data;

  return (
    <div className="space-y-6 animate-fade">
      {/* Page Header */}
      <PageHeader
        index="S36"
        eyebrow="Gestion Logistique · Paiements & Rapprochement"
        title="Payments Reconciliation (Imports & Bordereaux)"
        purpose="Rapprochement des versements financiers des transporteurs (recettes bancaires), importation des bordereaux et validation des lots d'encaissement."
        actions={
          <div className="flex items-center gap-2">
            <select
              value={selectedCarrier}
              onChange={(e) => setSelectedCarrier(e.target.value)}
              className="text-xs bg-card border border-border rounded-lg px-3 py-1.5 text-ink font-semibold shadow-xs cursor-pointer focus:outline-none focus:ring-1 focus:ring-clay-600"
            >
              <option value="all">Tous les transporteurs</option>
              {carriersQuery.data?.carriers.map((c) => (
                <option key={c.carrier} value={c.carrier}>
                  {c.carrier} ({c.count.toLocaleString('fr-FR')} colis)
                </option>
              ))}
            </select>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                settlementsQuery.refetch();
                paymentsSummaryQuery.refetch();
                historyQuery.refetch();
              }}
              disabled={settlementsQuery.isFetching}
            >
              <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${settlementsQuery.isFetching ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
          </div>
        }
      />
      
      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Panel className="p-4 border-border bg-card shadow-xs space-y-2">
          <div className="flex items-center justify-between text-xs text-ink-soft">
            <span className="font-semibold uppercase tracking-wider">Validé & Encaissé MaPara</span>
            <Coins className="h-4 w-4 text-herb-700" />
          </div>
          <div className="text-xl font-bold font-mono text-herb-800">
            {formatTnd(paymentSummary?.approvedAmount ?? 0)}
          </div>
          <p className="text-[11px] text-ink-faint">
            {(paymentSummary?.approvedCount ?? 0).toLocaleString('fr-FR')} colis validés en banque
          </p>
        </Panel>

        <Panel className="p-4 border-border bg-card shadow-xs space-y-2">
          <div className="flex items-center justify-between text-xs text-ink-soft">
            <span className="font-semibold uppercase tracking-wider">Déclaré Payé (Attente Val.)</span>
            <Clock className="h-4 w-4 text-amber-600" />
          </div>
          <div className="text-xl font-bold font-mono text-amber-800">
            {formatTnd(paymentSummary?.carrierPaidAmount ?? 0)}
          </div>
          <p className="text-[11px] text-ink-faint">
            {(paymentSummary?.carrierPaidCount ?? 0).toLocaleString('fr-FR')} colis déclarés par transporteur
          </p>
        </Panel>

        <Panel className="p-4 border-border bg-card shadow-xs space-y-2">
          <div className="flex items-center justify-between text-xs text-ink-soft">
            <span className="font-semibold uppercase tracking-wider">Total Livré (COD Attendu)</span>
            <ShieldCheck className="h-4 w-4 text-sky-600" />
          </div>
          <div className="text-xl font-bold font-mono text-ink">
            {formatTnd(paymentSummary?.totalDeliveredAmount ?? 0)}
          </div>
          <p className="text-[11px] text-ink-faint">
            {(paymentSummary?.totalDeliveredCount ?? 0).toLocaleString('fr-FR')} colis livrés au client
          </p>
        </Panel>

        <Panel className="p-4 border-border bg-card shadow-xs space-y-2">
          <div className="flex items-center justify-between text-xs text-ink-soft">
            <span className="font-semibold uppercase tracking-wider">Non Payé / Solde Restant</span>
            <span className="text-xs font-bold text-rose-700 bg-rose-50 px-2 py-0.5 rounded-full border border-rose-200">
              Dû
            </span>
          </div>
          <div className="text-xl font-bold font-mono text-rose-800">
            {formatTnd(paymentSummary?.unpaidAmount ?? 0)}
          </div>
          <p className="text-[11px] text-ink-faint">
            {(paymentSummary?.unpaidCount ?? 0).toLocaleString('fr-FR')} colis en attente de recette
          </p>
        </Panel>
      </div>

      {/* Upload Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Upload Settlement CSV */}
        <Panel className="p-5 border-emerald-600/30 bg-emerald-50/20 shadow-xs">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <FileSpreadsheet className="h-5 w-5 text-emerald-700" />
                <h3 className="text-base font-bold text-slate-900">Bordereau de Règlement Financier</h3>
              </div>
              <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                Importez les bordereaux de recette quotidiens (ex: <code>detail_recette_YYYY-MM-DD.csv</code>) pour
                rapprocher les montants effectivement versés sur le compte bancaire.
              </p>
            </div>
            <Button
              variant="primary"
              size="sm"
              className="bg-emerald-700 hover:bg-emerald-800 text-white shrink-0"
              onClick={() => settlementFileInputRef.current?.click()}
              disabled={uploadSettlementMutation.isPending}
            >
              <Upload className="h-4 w-4 mr-1.5" />
              {uploadSettlementMutation.isPending ? 'Importation…' : 'Importer Bordereau Recette'}
            </Button>
            <input
              type="file"
              ref={settlementFileInputRef}
              accept=".csv,.txt"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) uploadSettlementMutation.mutate(f);
              }}
            />
          </div>
        </Panel>

        {/* Upload Delivery CSV */}
        <Panel className="p-5 border-border bg-card shadow-xs">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-ink-soft" />
                <h3 className="text-base font-bold text-ink">Fichier de Dépose Transporteur</h3>
              </div>
              <p className="text-xs text-ink-soft mt-1 leading-relaxed">
                Importez le fichier CSV de suivi transmis par le transporteur (ex: <code>commande-mapara.csv</code>) pour
                détecter les colis fantômes et les écarts de prix COD.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="shrink-0"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadDeliveryMutation.isPending}
            >
              <Upload className="h-4 w-4 mr-1.5 text-ink-soft" />
              {uploadDeliveryMutation.isPending ? 'Importation…' : 'Importer CSV Dépose'}
            </Button>
            <input
              type="file"
              ref={fileInputRef}
              accept=".csv,.txt"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) uploadDeliveryMutation.mutate(f);
              }}
            />
          </div>
        </Panel>
      </div>

      {/* Settlements Table */}
      <Panel className="p-5 border-border bg-card shadow-xs space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-border">
          <div>
            <h3 className="text-base font-bold text-ink">Bordereaux de Règlement Enregistrés</h3>
            <p className="text-xs text-ink-soft">
              Historique des fichiers de recettes bancaires déclarés par les transporteurs.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="h-3.5 w-3.5 text-ink-ghost absolute left-2.5 top-2.5 pointer-events-none" />
              <input
                type="text"
                value={searchSettlement}
                onChange={(e) => setSearchSettlement(e.target.value)}
                placeholder="Rechercher fichier, réf…"
                className="text-xs bg-cream/40 border border-border rounded-lg pl-8 pr-3 py-1.5 text-ink focus:outline-none focus:ring-1 focus:ring-clay-600"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as 'all' | 'pending' | 'approved')}
              className="text-xs bg-cream/40 border border-border rounded-lg px-2.5 py-1.5 text-ink font-medium cursor-pointer"
            >
              <option value="all">Tous statuts</option>
              <option value="pending">En attente</option>
              <option value="approved">Validés</option>
            </select>
          </div>
        </div>

        <TableShell>
          <Table>
            <THead>
              <Tr>
                <Th>Date Règlement</Th>
                <Th>Fichier</Th>
                <Th>Transporteur</Th>
                <Th align="right">Montant Total</Th>
                <Th align="right">Colis</Th>
                <Th>Statut Validation</Th>
                <Th>Réf Bancaire / Note</Th>
                <Th align="right">Action Admin</Th>
              </Tr>
            </THead>
            <TBody>
              {filteredSettlements.map((s) => (
                <Tr key={s.id}>
                  <Td>
                    <span className="font-mono text-xs font-semibold text-ink">
                      {s.settlementDate ? formatDateFr(s.settlementDate) : '—'}
                    </span>
                  </Td>
                  <Td>
                    <span className="font-mono text-xs text-ink-soft">{s.filename || '—'}</span>
                  </Td>
                  <Td>
                    <span className="text-xs font-medium text-ink">{s.carrierName}</span>
                  </Td>
                  <Td align="right">
                    <span className="font-mono text-xs font-bold text-ink">{formatTnd(s.totalAmount)}</span>
                  </Td>
                  <Td align="right">
                    <span className="font-mono text-xs text-ink">{s.parcelCount.toLocaleString('fr-FR')}</span>
                  </Td>
                  <Td>
                    {s.status === 'approved' ? (
                      <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-800 bg-emerald-50 border border-emerald-300 px-2 py-0.5 rounded-full">
                        <Check className="h-3 w-3 text-emerald-700" />
                        Validé en banque
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-900 bg-amber-50 border border-amber-300 px-2 py-0.5 rounded-full">
                        <Clock className="h-3 w-3 text-amber-700" />
                        En attente
                      </span>
                    )}
                  </Td>
                  <Td>
                    <span className="text-xs font-mono text-ink-soft">
                      {s.bankRef || s.notes || '—'}
                    </span>
                  </Td>
                  <Td align="right">
                    {s.status !== 'approved' && (
                      <Button
                        variant="outline"
                        size="xs"
                        className="text-xs text-emerald-800 border-emerald-600/40 hover:bg-emerald-50 cursor-pointer"
                        onClick={() => {
                          setTargetSettlementId(s.id);
                          setApprovalModalOpen(true);
                        }}
                      >
                        <CheckCheck className="h-3.5 w-3.5 mr-1 text-emerald-700" />
                        Valider le lot
                      </Button>
                    )}
                  </Td>
                </Tr>
              ))}
              {filteredSettlements.length === 0 && (
                <Tr>
                  <Td colSpan={8} className="text-center py-8 text-ink-ghost text-xs">
                    Aucun bordereau ne correspond aux filtres sélectionnés.
                  </Td>
                </Tr>
              )}
            </TBody>
          </Table>
        </TableShell>
      </Panel>

      {/* Import History */}
      <Panel className="p-5 border-border bg-card shadow-xs space-y-3">
        <h3 className="text-base font-bold text-ink">Historique des Synchronisations & Imports</h3>
        <p className="text-xs text-ink-soft">Dernières opérations d'ingestion exécutées sur le pipeline.</p>
        <TableShell>
          <Table>
            <THead>
              <Tr>
                <Th>Type</Th>
                <Th>Date & Heure</Th>
                <Th>Déclenché par</Th>
                <Th align="right">Lignes traitées</Th>
                <Th>Statut</Th>
              </Tr>
            </THead>
            <TBody>
              {(historyQuery.data?.syncs ?? []).slice(0, 10).map((h) => (
                <Tr key={h.id}>
                  <Td>
                    <span className="text-xs font-semibold text-slate-800">Shiparon MySQL Sync</span>
                  </Td>
                  <Td>
                    <span className="text-xs font-mono text-slate-600">{h.createdAt ? formatDateFr(h.createdAt) : '—'}</span>
                  </Td>
                  <Td>
                    <span className="text-xs text-slate-700">{h.triggeredBy || 'Système'}</span>
                  </Td>
                  <Td align="right">
                    <span className="text-xs font-mono font-bold text-slate-900">{h.rowCount.toLocaleString('fr-FR')}</span>
                  </Td>
                  <Td>
                    <span
                      className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${
                        h.status === 'completed'
                          ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                          : h.status === 'running'
                          ? 'bg-amber-50 text-amber-800 border border-amber-200'
                          : 'bg-rose-50 text-rose-800 border border-rose-200'
                      }`}
                    >
                      {h.status}
                    </span>
                  </Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        </TableShell>
      </Panel>

      {/* Approval Modal */}
      {approvalModalOpen &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="fixed inset-0 z-[999] flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 overflow-y-auto animate-fade"
            onClick={() => setApprovalModalOpen(false)}
          >
            <div
              role="dialog"
              aria-modal="true"
              className="relative bg-card border border-border rounded-2xl p-6 max-w-md w-full shadow-2xl space-y-4 my-auto animate-in zoom-in-95 duration-150"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between pb-2 border-b border-border">
                <div className="flex items-center gap-2">
                  <CheckCheck className="h-5 w-5 text-emerald-700" />
                  <h3 className="text-base font-bold text-ink">Valider l'encaissement du bordereau</h3>
                </div>
                <button
                  type="button"
                  onClick={() => setApprovalModalOpen(false)}
                  className="text-ink-ghost hover:text-ink p-1 rounded-lg"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <p className="text-xs text-ink-soft leading-relaxed">
                Cette action marquera tous les colis de ce bordereau comme{' '}
                <strong className="text-emerald-800">« Encaissé & Validé MaPara »</strong> dans le grand livre de réconciliation.
              </p>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-ink mb-1">
                    Référence Virement / Chèque Bancaire
                  </label>
                  <input
                    type="text"
                    value={approvalBankRef}
                    onChange={(e) => setApprovalBankRef(e.target.value)}
                    placeholder="ex: VIR-BIAT-20261003-882"
                    className="w-full text-xs bg-cream/40 border border-border rounded-lg px-3 py-2 text-ink focus:outline-none focus:ring-1 focus:ring-clay-600 font-mono"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-ink mb-1">
                    Note d'audit (optionnel)
                  </label>
                  <textarea
                    value={approvalNotes}
                    onChange={(e) => setApprovalNotes(e.target.value)}
                    rows={2}
                    placeholder="Remarques comptables ou justification…"
                    className="w-full text-xs bg-cream/40 border border-border rounded-lg px-3 py-2 text-ink focus:outline-none focus:ring-1 focus:ring-clay-600"
                  />
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
                <Button variant="outline" size="sm" onClick={() => setApprovalModalOpen(false)}>
                  Annuler
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  className="bg-emerald-700 hover:bg-emerald-800 text-white"
                  disabled={approveMutation.isPending}
                  onClick={() => {
                    if (targetSettlementId) {
                      approveMutation.mutate({
                        settlementId: targetSettlementId,
                        bankRef: approvalBankRef.trim() || undefined,
                        notes: approvalNotes.trim() || undefined,
                      });
                    }
                  }}
                >
                  {approveMutation.isPending ? 'Validation…' : 'Confirmer la validation'}
                </Button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
